export const StepType = {
  PAGE_DETECTION: "PAGE_DETECTION",
  OCR: "OCR",
  DATA_EXTRACTION: "DATA_EXTRACTION",
} as const;

export type StepType = (typeof StepType)[keyof typeof StepType];

export const FlowType = {
  FILE_ONLY: "FILE_ONLY",
  BASIC: "BASIC",
  PAGE_DETECTION: "PAGE_DETECTION",
  NO_OCR_PAGE_DETECTION: "NO_OCR_PAGE_DETECTION",
  PARALLEL: "PARALLEL",
} as const;

export type FlowType = (typeof FlowType)[keyof typeof FlowType];

export const ProviderType = {
  OCR: "OCR",
  LLM: "LLM",
};

export type ProviderType = (typeof ProviderType)[keyof typeof ProviderType];

// ============================================================================
// Document Type Models
// ============================================================================

/** Doctype → mapping-service routing. When `enabled`, completed items of this
 *  doctype are dispatched to the mapping service `(service, plugin_id)`. */
export interface MappingConfigModel {
  enabled: boolean;
  service: string | null;
  plugin_id: string | null;
}

export interface MappingConfigViewModel {
  enabled: boolean;
  service: string | null;
  pluginId: string | null;
}

export interface DocumentTypeDetailModel {
  id: string;
  doctype_code: string;
  doctype_name: string;
  logo_url: string | null;
  description: string | null;
  category_id: string | null;
  category_name: string | null;
  organization_id: string | null;
  status: string;
  mapping_config: MappingConfigModel | null;
  sharepoint_enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
  /** The live flow version; null when none is active. */
  active_flow?: FlowVersionSummaryResponse | null;
}

export interface DocumentTypeDetailViewModel {
  id: string;
  docTypeCode: string;
  docTypeName: string;
  logoUrl: string | null;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  organizationId: string | null;
  status: string;
  mappingConfig: MappingConfigViewModel | null;
  sharepointEnabled: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
  /**
   * The live flow version; null when none is active, undefined when the API
   * did not say (an extraction service without `active_flow`).
   */
  activeFlow?: FlowVersionSummaryViewModel | null;
}

export interface CreateDocumentTypeRequest {
  docTypeCode: string;
  docTypeName: string;
  categoryId: string;
  description?: string;
  status?: string;
  logo?: File;
  mappingEnabled?: boolean;
  mappingService?: string | null;
  mappingPluginId?: string | null;
  sharepointConfig?: SharePointConnectionInput;
}

export interface SharePointConnectionInput {
  hostname: string;
  site_path: string;
  root_folder: string;
  tenant_id: string;
  client_id: string;
  client_secret: string;
}

export interface UpdateDocumentTypeRequest {
  docTypeCode?: string;
  docTypeName?: string;
  categoryId?: string;
  description?: string;
  status?: string;
  logo?: File;
  mappingEnabled?: boolean;
  mappingService?: string | null;
  mappingPluginId?: string | null;
  sharepointConfig?: SharePointConnectionInput;
  sharepointRemove?: boolean;
}

// ============================================================================
// Flow Configuration Models
//
// The extraction service's strict flow contract (docs/FLOW_CONFIG_REFERENCE.md
// there): every object rejects unknown keys, absent or null inherits the
// default, and false / 0 are real values. Model fields hold "provider:model"
// references; the view models split them into provider + model for editing.
// ============================================================================

export type InputMode = "ocr" | "document" | "document_with_ocr" | "document_with_tables";

/** `config_options` of an LLM step or agent. */
export interface LLMConfigOptions {
  temperature?: number | null;
  top_p?: number | null;
  top_k?: number | null;
  max_output_tokens?: number | null;
  thinking_budget?: number | null;
  seed?: number | null;
  media_resolution?: "low" | "medium" | "high" | null;
  compact_response_schema?: boolean | null;
  /** DATA_EXTRACTION only (rejected on PAGE_DETECTION). */
  input_mode?: InputMode | null;
  missing_ocr_policy?: "fallback" | "fail" | null;
  ocr_min_confidence?: number | null;
  ocr_bbox_mode?: "full" | "coarse" | "none" | null;
}

/** PAGE_DETECTION only. */
export interface ChunkingOptions {
  min_chunk_size?: number | null;
  chunk_overlap?: number | null;
  default_max_pages_per_domain?: number | null;
  max_pages_per_domain?: Record<string, number> | null;
  span_closure?: {
    back?: number | null;
    domains?: string[] | null;
  } | null;
  prompt_cache?: boolean | null;
}

/** Multi-agent DATA_EXTRACTION only. */
export interface PageDetectionPolicyOptions {
  unreported_domain_policy?: "skip" | "run" | "fail" | null;
  empty_domain_policy?: "skip" | "run" | "fail" | null;
}

export interface PreprocessingOptions {
  strip_pdf_links?: boolean | null;
}

export interface OCROptions {
  /** 0–100. */
  min_ocr_confidence?: number | null;
  locale?: string | null;
  /** Document Intelligence page selection, e.g. `1-3,5`. */
  pages?: string | null;
}

/** Step-level options the runtime tab edits. */
export interface StepRuntimeOptionsValue {
  fallbackModel?: string | null;
  chunkingOptions?: ChunkingOptions | null;
  pageDetectionPolicy?: PageDetectionPolicyOptions | null;
}

export interface AgentRuntimeOptionsValue {
  fallbackModel?: string | null;
}

export interface LLMConfigOptionsViewModel {
  temperature?: number | null;
  topP?: number | null;
  topK?: number | null;
  maxOutputTokens?: number | null;
  thinkingBudget?: number | null;
  seed?: number | null;
  mediaResolution?: "low" | "medium" | "high" | null;
  compactResponseSchema?: boolean | null;
  inputMode?: InputMode | null;
  missingOcrPolicy?: "fallback" | "fail" | null;
  ocrMinConfidence?: number | null;
  ocrBboxMode?: "full" | "coarse" | "none" | null;
}

export interface LLMFlowStepConfigResponse {
  system_prompt: string;
  user_prompt?: string | null;
  model_name: string;
  fallback_model?: string | null;
  config_options?: LLMConfigOptions | null;
  chunking_options?: ChunkingOptions | null;
  page_detection_policy?: PageDetectionPolicyOptions | null;
  response_schema?: Record<string, unknown> | null;
  executions?: AgentExecutionResponse[] | null;
}

export interface LLMFlowStepConfigViewModel {
  /** Catalog provider key, from the model reference's prefix; empty for a bare alias. */
  provider: string;
  systemPrompt: string;
  /** Empty for a multi-agent step (each agent has its own). */
  userPrompt: string;
  /** The model id after the provider prefix. */
  modelName: string;
  /** The reference's prefix as stored, reused on save while the provider is unchanged. */
  modelPrefix?: string;
  configOptions: LLMConfigOptionsViewModel | null;
  fallbackModel?: string | null;
  chunkingOptions?: ChunkingOptions | null;
  pageDetectionPolicy?: PageDetectionPolicyOptions | null;
  responseSchema: Record<string, unknown> | null;
  executions: AgentExecutionViewModel[] | null;
}

export interface OCRFlowStepConfigResponse {
  model_name: string;
  ocr_options?: OCROptions | null;
}

export interface OCRFlowStepConfigViewModel {
  provider: string;
  modelName: string;
  modelPrefix?: string;
  ocrOptions: OCROptions | null;
}

// ============================================================================
// Multi-agent DATA_EXTRACTION
// ============================================================================

export interface AgentExecutionResponse {
  domain_key: string;
  user_prompt: string;
  response_schema: Record<string, unknown>;
  system_prompt?: string | null;
  model_name?: string | null;
  fallback_model?: string | null;
  config_options?: LLMConfigOptions | null;
}

export interface AgentExecutionViewModel {
  domainKey: string;
  userPrompt: string;
  responseSchema: Record<string, unknown>;
  systemPrompt: string | null;
  /** Provider of the agent's own model; empty when it inherits the step model. */
  provider: string;
  /** Empty: the agent runs on the step's model. */
  modelName: string | null;
  modelPrefix?: string;
  configOptions: LLMConfigOptionsViewModel | null;
  fallbackModel?: string | null;
}

export interface FlowStepResponse {
  step_order: number;
  step_type: string;
  llm_step_config?: LLMFlowStepConfigResponse | null;
  ocr_step_config?: OCRFlowStepConfigResponse | null;
}

export interface FlowStepViewModel {
  stepOrder: number;
  stepType: string;
  llmStepConfig?: LLMFlowStepConfigViewModel | null;
  ocrStepConfig?: OCRFlowStepConfigViewModel | null;
}

// Flow version summary (for listing)
export interface FlowVersionSummaryResponse {
  id: string;
  flow_base_id: string;
  version: number;
  is_active: boolean;
  flow_type: string;
  created_at: string | null;
  updated_at: string | null;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface FlowVersionSummaryViewModel {
  id: string;
  flowBaseId: string;
  version: number;
  isActive: boolean;
  flowType: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  createdBy?: string | null;
  updatedBy?: string | null;
}

// Full flow configuration (for detail view)
export interface FlowConfigResponse {
  id: string;
  flow_base_id: string;
  doctype_id: string;
  flow_type: string;
  flow_description: string | null;
  version: number;
  steps: FlowStepResponse[];
  preprocessing_options?: PreprocessingOptions | null;
  is_active: boolean;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface FlowConfigViewModel {
  id: string;
  flowBaseId: string;
  docTypeId: string;
  flowType: string;
  flowDescription: string | null;
  version: number;
  steps: FlowStepViewModel[];
  preprocessingOptions?: PreprocessingOptions | null;
  isActive: boolean;
  updatedBy: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

// ============================================================================
// Request Models
// ============================================================================

export interface LLMFlowStepConfigRequest {
  system_prompt: string;
  user_prompt?: string | null;
  model_name: string;
  fallback_model?: string | null;
  config_options?: LLMConfigOptions | null;
  chunking_options?: ChunkingOptions | null;
  page_detection_policy?: PageDetectionPolicyOptions | null;
  response_schema?: Record<string, unknown> | null;
  executions?: AgentExecutionRequest[] | null;
}

export interface OCRFlowStepConfigRequest {
  model_name: string;
  ocr_options?: OCROptions | null;
}

export interface AgentExecutionRequest {
  domain_key: string;
  user_prompt: string;
  response_schema: Record<string, unknown>;
  system_prompt?: string | null;
  model_name?: string | null;
  fallback_model?: string | null;
  config_options?: LLMConfigOptions | null;
}

export interface FlowStepRequest {
  step_order: number;
  step_type: string;
  llm_step_config?: LLMFlowStepConfigRequest;
  ocr_step_config?: OCRFlowStepConfigRequest;
}

export interface CreateFlowConfigRequest {
  flow_base_id?: string | null;
  flow_type: string;
  flow_description?: string | null;
  steps: FlowStepRequest[];
  preprocessing_options?: PreprocessingOptions | null;
}

export interface ActivateFlowRequest {
  flow_base_id: string;
  version: number;
}
